// items-physics.js — Probe item bank: PHYSICS (calibration-adjacent domain)
// =============================================================================
// One probe per physics node, keyed to the REAL registry node ids in
// graph-data.js. Physics sits just downstream of the math spine (calculus /
// linear_algebra / differential_equations gate it), so it's the natural next
// domain after mathematics: right/wrong is still largely unambiguous, but the
// probes here test PHYSICAL reasoning rather than symbol manipulation.
//
// AUTHORING STANDARD (identical to items-math.js — every probe satisfies all):
//   1. DIAGNOSTIC, not trivia. Correct answer requires grasping the node's core
//      physical idea, not recalling a name or formula.
//   2. ONE concept. Each probe targets the single most load-bearing idea, so a
//      miss cleanly implicates THAT node (and its DAG descendants).
//   3. PREREQUISITE-CLEAN. Answering must not secretly require a HARDER topic,
//      NOR require doing the math prerequisite. A classical-mechanics probe
//      tests Newtonian *reasoning*, not whether you can integrate — otherwise a
//      miss would ambiguously implicate calculus rather than the physics.
//      (This is the key subtlety for physics: we probe the CONCEPT the node
//      adds on top of its math prereqs, not the math itself.)
//   4. DISTRACTORS ARE DIAGNOSTIC. Wrong options encode the specific canonical
//      misconception for that topic (noted per item in `trap`), so a wrong
//      answer is informative rather than random.
//   5. STABLE & UNAMBIGUOUS. Exactly one defensible answer; no "it depends."
//
// Schema per item (merged onto the registry node at load time):
//   { q, opts:[4], a:index, concept, trap }  — a = index of the correct option.
// =============================================================================

export const PHYSICS_ITEMS = {

  // --- classical_mechanics (prereq: calculus) --------------------------------
  // Load-bearing idea: Newton's 1st/2nd law — force causes ACCELERATION, not
  // velocity. The deepest, most persistent pre-Newtonian ("impetus") error.
  classical_mechanics: {
    q: 'A hockey puck slides across frictionless ice at constant velocity. What is the net force on it?',
    opts: [
      'Zero — no net force is needed to keep it moving',
      'A constant forward force equal to its weight',
      'A small forward force to overcome its inertia',
      'A force that decreases as the puck slows down'
    ],
    a: 0,
    concept: 'Newton\u2019s first law: constant velocity means zero net force. Force produces acceleration (a change in motion), not motion itself.',
    trap: 'Choosing any "forward force" is the Aristotelian/impetus misconception \u2014 believing motion requires a sustaining force. It is the single most documented misconception in physics education (the FCI is built around it). A miss here should cap belief in ALL downstream physics.'
  },

  // --- electromagnetism (prereq: calculus, differential_equations) -----------
  // Load-bearing idea: a CHANGING field is the source of the other field
  // (Faraday/Maxwell). Static charge alone does not radiate or induce.
  electromagnetism: {
    q: 'What is required to induce a current in a stationary closed loop of wire?',
    opts: [
      'A steady magnetic field passing through the loop',
      'A magnetic field through the loop that is changing in time',
      'A strong static electric charge held near the loop',
      'Any magnetic field, as long as it is strong enough'
    ],
    a: 1,
    concept: 'Faraday\u2019s law: EMF is induced by the rate of CHANGE of magnetic flux, not by the field\u2019s magnitude. A static field \u2014 however strong \u2014 induces nothing.',
    trap: 'Picking "steady" or "strong static" field reveals the core misconception that fields cause effects by presence rather than by change \u2014 the conceptual heart of electromagnetism a learner must cross. It also cleanly separates EM understanding from mere electrostatics.'
  },

  // --- thermodynamics (prereq: calculus soft, diff-eq soft) ------------------
  // Load-bearing idea: 2nd law / entropy direction. Heat flows hot->cold
  // spontaneously; the reverse requires work input.
  thermodynamics: {
    q: 'Two objects at different temperatures are placed in contact and isolated from everything else. What happens?',
    opts: [
      'Heat flows from the hotter to the colder until temperatures equalize',
      'Heat flows from the colder to the hotter, since cold seeks heat',
      'Both objects cool down together toward absolute zero',
      'Nothing, unless external work is supplied to move the heat'
    ],
    a: 0,
    concept: 'Second law: heat spontaneously flows from higher to lower temperature, increasing total entropy, until thermal equilibrium. The reverse never happens on its own.',
    trap: '"Cold seeks heat" reflects treating cold as a substance that flows (the caloric-era misconception); the "external work" option confuses the spontaneous case with a refrigerator. Both reveal a missing grasp of entropy\u2019s directionality \u2014 the concept thermodynamics is built on.'
  },

  // --- quantum_mechanics (prereq: linear_algebra, diff-eq, class_mech) -------
  // Load-bearing idea: superposition + measurement, NOT "we just don't know
  // yet." The state is genuinely indeterminate until measured.
  quantum_mechanics: {
    q: 'Before measurement, an electron\u2019s spin is in a superposition of up and down. What does this mean?',
    opts: [
      'The electron is already up or down; we simply don\u2019t know which yet',
      'The electron has no definite spin value until it is measured',
      'The electron is rapidly flipping between up and down',
      'The electron is spinning in a sideways direction we can\u2019t see'
    ],
    a: 1,
    concept: 'Superposition is genuine indeterminacy, not hidden information: the observable has no definite value until measurement collapses the state. This is what distinguishes quantum from classical probability.',
    trap: 'Option 0 is the "hidden variables / classical ignorance" misconception \u2014 the intuitive but wrong belief that quantum uncertainty is just incomplete knowledge (ruled out by Bell). Options 2/3 are "secret classical motion" evasions. Detecting these separates real QM understanding from a classical picture wearing quantum vocabulary.'
  },

  // --- relativity (prereq: classical_mechanics) ------------------------------
  // Load-bearing idea: constancy of c for all observers -> relativity of
  // simultaneity/time. Velocities do NOT simply add.
  relativity: {
    q: 'A spaceship moving at 0.9c shines a laser forward. How fast does the light travel relative to a stationary observer?',
    opts: [
      '1.9c \u2014 the ship\u2019s speed plus the speed of light',
      'c \u2014 the same speed of light measured by everyone',
      '0.9c \u2014 slowed by the ship\u2019s motion',
      'It depends on the observer\u2019s own speed'
    ],
    a: 1,
    concept: 'The second postulate: the speed of light in vacuum is c for every inertial observer, regardless of the source\u2019s motion. Galilean velocity addition fails at high speed.',
    trap: 'Picking 1.9c is the Galilean velocity-addition error \u2014 applying everyday intuition where it breaks down. It is THE gateway misconception of special relativity; getting past it is the entire conceptual leap. Option 3 mistakes the invariance of c for ordinary relativity of velocity.'
  },

  // --- optics (prereq: electromagnetism) -------------------------------------
  // Load-bearing idea: refraction = speed change at a boundary bends light
  // toward the normal entering a denser medium (Snell). Frequency is fixed;
  // wavelength/speed change.
  optics: {
    q: 'A ray of light passes from air into water (a denser medium). What happens to its direction?',
    opts: [
      'It bends toward the normal, because it slows down in water',
      'It bends away from the normal, because it speeds up in water',
      'It continues in a straight line; only its color changes',
      'It reflects entirely back into the air'
    ],
    a: 0,
    concept: 'Refraction: entering a slower (higher-index) medium, light bends toward the normal. The wave slows and its wavelength shortens while frequency stays constant.',
    trap: 'Option 1 inverts the speed relationship (thinking denser = faster); option 2 ignores refraction entirely. Both reveal a missing model of light as a wave whose speed depends on the medium \u2014 the foundation optics adds on top of electromagnetism.'
  },

  // --- nuclear_physics (prereq: quantum_mechanics) ---------------------------
  // Load-bearing idea: mass-energy equivalence + binding energy is the source
  // of nuclear energy (mass defect), NOT chemical bonds.
  nuclear_physics: {
    q: 'Where does the energy released in nuclear fission actually come from?',
    opts: [
      'From breaking the chemical bonds between atoms',
      'From a tiny amount of mass converting into energy (E = mc\u00b2)',
      'From the heat already stored inside the heavy nucleus',
      'From the kinetic energy of the neutron that starts the reaction'
    ],
    a: 1,
    concept: 'Nuclear energy comes from the mass defect: the products have slightly less mass than the reactants, and that missing mass appears as energy via E = mc\u00b2. Binding energy per nucleon is the key quantity.',
    trap: 'Option 0 conflates nuclear with chemical energy \u2014 the single most common confusion, and off by ~6 orders of magnitude. Option 3 mistakes the trigger for the source. Detecting these confirms the learner grasps that nuclear energy is a mass-energy phenomenon, the concept the node is built on.'
  },
};

// Convenience: attach items to registry node records.
// Usage in the diagnostic: node.question = PHYSICS_ITEMS[node.id] || null;
export function attachPhysicsItems(nodes) {
  return nodes.map(n => (PHYSICS_ITEMS[n.id] ? { ...n, question: PHYSICS_ITEMS[n.id] } : n));
}
